import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import '@fontsource-variable/geist/wght.css';
import { IconBuilding, IconCertificate, IconFilterPlus, IconStack2 } from '@tabler/icons-react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { EcClickUpFilterChip } from '../../experiencia-cliente/components/EcClickUpFilterChip';
import {
  buildEcFilterPopoverStyle,
  EC_FILTER_POPOVER_CLASS,
  useEcFilterPopoverLayout,
} from '../../experiencia-cliente/components/experienciaClienteFilterPopoverUtils';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../ConfigStatusView2.css';
import ProcessConfigSkeleton from '../ConfigStatusView2Skeleton';
import './DefeitosEquipCompatModal.css';
import './MarcaSubgruposCadastrosConfig.css';

const API_BASE_URL = getApiBaseUrl();
const MSCAD_FILTER_POPOVER_Z = 10050;
const pendingSubgroupPrefix = '__mscad_sg__:';

const AVATAR_PALETTES = [
  { bg: '#e0f2fe', text: '#0369a1' },
  { bg: '#fef3c7', text: '#b45309' },
  { bg: '#d1fae5', text: '#047857' },
  { bg: '#ffe4e6', text: '#be123c' },
  { bg: '#ffedd5', text: '#c2410c' },
  { bg: '#dbeafe', text: '#1d4ed8' },
];

function withMscadPopoverStyle(layoutStyle) {
  if (!layoutStyle) return null;
  return { ...layoutStyle, zIndex: MSCAD_FILTER_POPOVER_Z };
}

function marcaInitials(nome) {
  const s = String(nome || '').trim();
  if (!s) return '?';
  const parts = s.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function paletteForMarca(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h + s.charCodeAt(i)) % AVATAR_PALETTES.length;
  return AVATAR_PALETTES[h];
}

function Pcs2Subblock({ title, desc, children, hideHead = false }) {
  return (
    <div className="pcs2-subblock">
      {!hideHead && (title || desc) ? (
        <div className="pcs2-subhead">
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
        </div>
      ) : null}
      {children}
    </div>
  );
}

function Pcs2Section({ icon: Icon, title, desc, children, className }) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div className="pcs2-section-aside">
        <div className="pcs2-section-heading">
          <Icon size={16} stroke={1.75} aria-hidden />
          <h3 className="pcs2-section-title">{title}</h3>
        </div>
        <p className="pcs2-section-desc">{desc}</p>
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function MscadCheckbox({ checked, onClick, title, disabled }) {
  return (
    <label
      className={`deq-check-label${disabled ? ' mscad-check--busy' : ''}`}
      title={title}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="deq-check-native"
        checked={checked}
        disabled={disabled}
        onChange={() => onClick()}
        onClick={(e) => e.stopPropagation()}
        aria-label={title}
      />
      <span className="deq-check-box" aria-hidden />
    </label>
  );
}

function MarcaAvatar({ fabricante }) {
  const p = paletteForMarca(fabricante);
  return (
    <span className="deq-avatar" style={{ background: p.bg, color: p.text }}>
      {marcaInitials(fabricante)}
    </span>
  );
}

function MscadFiltersEmptyState() {
  return (
    <div className="deq-empty deq-empty--modern">
      <div className="deq-empty-placeholder">
        <IconFilterPlus size={40} stroke={1.25} className="deq-empty-icon" aria-hidden />
        <h3 className="deq-empty-modern-title">Aplique os filtros</h3>
        <p className="deq-empty-modern-desc">
          Escolha fabricante e/ou tecnologia, ou digite parte do nome (mín. 2 caracteres) para listar
          fabricantes e tecnologias disponíveis.
        </p>
      </div>
    </div>
  );
}

export default function MarcaSubgruposCadastrosConfig() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState(null);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [linkedCollapsed, setLinkedCollapsed] = useState(() => new Set());
  const [fabricantes, setFabricantes] = useState([]);
  const [tecnologias, setTecnologias] = useState([]);
  const [busca, setBusca] = useState('');
  const [buscaDeb, setBuscaDeb] = useState('');
  const [fabOpen, setFabOpen] = useState(false);
  const [tecOpen, setTecOpen] = useState(false);
  const [fabSearch, setFabSearch] = useState('');
  const [tecSearch, setTecSearch] = useState('');

  const fabBtnRef = useRef(null);
  const tecBtnRef = useRef(null);
  const fabLayout = useEcFilterPopoverLayout(fabBtnRef, fabOpen, 236, 320);
  const tecLayout = useEcFilterPopoverLayout(tecBtnRef, tecOpen, 248, 360);
  const fabPopoverStyle = buildEcFilterPopoverStyle(fabLayout);
  const tecPopoverStyle = buildEcFilterPopoverStyle(tecLayout);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/marcas-subgrupos-models`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await res.json();
      if (result.success && Array.isArray(result.data)) setRows(result.data);
      else setRows([]);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDeb(busca.trim()), 380);
    return () => clearTimeout(t);
  }, [busca]);

  useEffect(() => {
    if (!fabOpen) setFabSearch('');
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) setTecSearch('');
  }, [tecOpen]);

  useEffect(() => {
    if (!fabOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-mscad-fab-popover]');
      if (pop?.contains(e.target) || fabBtnRef.current?.contains(e.target)) return;
      setFabOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [fabOpen]);

  useEffect(() => {
    if (!tecOpen) return undefined;
    const onDoc = (e) => {
      const pop = document.querySelector('[data-mscad-tec-popover]');
      if (pop?.contains(e.target) || tecBtnRef.current?.contains(e.target)) return;
      setTecOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [tecOpen]);

  const byBrand = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const bid = String(r.id_brand ?? '').trim();
      const sid = String(r.id_subgroup ?? '').trim();
      if (!bid || !sid) continue;
      const en = r.enabled === 1 || r.enabled === true;
      if (!map.has(bid)) {
        map.set(bid, {
          id_brand: bid,
          brand_nome: r.brand_nome || bid,
          subById: new Map(),
        });
      }
      const g = map.get(bid);
      if (r.brand_nome) g.brand_nome = r.brand_nome;
      if (!g.subById.has(sid)) {
        g.subById.set(sid, {
          id_subgroup: sid,
          subgroup_nome: r.subgroup_nome || sid,
          enabled: en,
        });
      } else {
        const cur = g.subById.get(sid);
        cur.enabled = cur.enabled || en;
        if (r.subgroup_nome) cur.subgroup_nome = r.subgroup_nome;
      }
    }
    const list = Array.from(map.values()).map((g) => ({
      id_brand: g.id_brand,
      brand_nome: g.brand_nome,
      subgrupos: Array.from(g.subById.values()),
    }));
    list.sort((a, b) => String(a.brand_nome).localeCompare(String(b.brand_nome), 'pt-BR'));
    for (const g of list) {
      g.subgrupos.sort((x, y) =>
        String(x.subgroup_nome).localeCompare(String(y.subgroup_nome), 'pt-BR')
      );
    }
    return list;
  }, [rows]);

  const marcaItems = useMemo(
    () => byBrand.map((g) => ({ value: g.brand_nome, label: g.brand_nome })),
    [byBrand]
  );

  const tecnologiaItems = useMemo(() => {
    const set = new Set();
    for (const g of byBrand) {
      for (const s of g.subgrupos) {
        const n = String(s.subgroup_nome || '').trim();
        if (n) set.add(n);
      }
    }
    return [...set]
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((n) => ({ value: n, label: n }));
  }, [byBrand]);

  const filteredFabItems = useMemo(() => {
    const q = fabSearch.trim().toLowerCase();
    if (!q) return marcaItems;
    return marcaItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [marcaItems, fabSearch]);

  const filteredTecItems = useMemo(() => {
    const q = tecSearch.trim().toLowerCase();
    if (!q) return tecnologiaItems;
    return tecnologiaItems.filter((i) => String(i.label).toLowerCase().includes(q));
  }, [tecnologiaItems, tecSearch]);

  const canShowList =
    fabricantes.length > 0 || tecnologias.length > 0 || buscaDeb.trim().length >= 2;

  const filteredByBrand = useMemo(() => {
    if (!canShowList) return [];

    let list = byBrand;

    if (fabricantes.length > 0) {
      const sel = new Set(fabricantes);
      list = list.filter((g) => sel.has(g.brand_nome));
    }

    if (tecnologias.length > 0) {
      const sel = new Set(tecnologias);
      list = list
        .map((g) => ({
          ...g,
          subgrupos: g.subgrupos.filter((s) => sel.has(s.subgroup_nome)),
        }))
        .filter((g) => g.subgrupos.length > 0);
    }

    const q = buscaDeb.trim().toLowerCase();
    if (q.length >= 2) {
      list = list
        .map((g) => {
          const brandMatch =
            String(g.brand_nome || '')
              .toLowerCase()
              .includes(q) || String(g.id_brand || '').toLowerCase().includes(q);
          const subgrupos = brandMatch
            ? g.subgrupos
            : g.subgrupos.filter(
                (s) =>
                  String(s.subgroup_nome || '')
                    .toLowerCase()
                    .includes(q) ||
                  String(s.id_subgroup || '')
                    .toLowerCase()
                    .includes(q)
              );
          if (!brandMatch && subgrupos.length === 0) return null;
          return { ...g, subgrupos };
        })
        .filter(Boolean);
    }

    return list;
  }, [byBrand, fabricantes, tecnologias, buscaDeb, canShowList]);

  const enabledByBrand = useMemo(
    () =>
      byBrand
        .map((g) => ({
          ...g,
          subgrupos: g.subgrupos.filter((s) => s.enabled),
        }))
        .filter((g) => g.subgrupos.length > 0),
    [byBrand]
  );

  const enabledCount = useMemo(
    () => enabledByBrand.reduce((n, g) => n + g.subgrupos.length, 0),
    [enabledByBrand]
  );

  const hasFilters = fabricantes.length > 0 || tecnologias.length > 0 || busca !== '';

  useEffect(() => {
    if (loading || !canShowList) return;
    setCollapsed((prev) => {
      const next = new Set(prev);
      filteredByBrand.forEach((g) => next.delete(g.brand_nome));
      return next;
    });
  }, [loading, canShowList, filteredByBrand]);

  const rowKey = (idBrand, idSubgroup) => `${idBrand}::${idSubgroup}`;

  const isSubgroupRowPending = (idSubgroup) =>
    pendingKey === `${pendingSubgroupPrefix}${String(idSubgroup).trim()}`;

  const setEnabled = async (idBrand, idSubgroup, enabled) => {
    const key = rowKey(idBrand, idSubgroup);
    const sgNorm = String(idSubgroup).trim();
    setPendingKey(enabled ? `${pendingSubgroupPrefix}${sgNorm}` : key);
    const prevRows = rows;
    setRows((r) =>
      enabled
        ? r.map((x) => (String(x.id_subgroup).trim() === sgNorm ? { ...x, enabled: 1 } : x))
        : r.map((x) =>
            String(x.id_brand).trim() === idBrand && String(x.id_subgroup).trim() === sgNorm
              ? { ...x, enabled: 0 }
              : x
          )
    );
    try {
      const token = localStorage.getItem('authToken');
      const url = enabled
        ? `${API_BASE_URL}/api/config/marcas-subgrupos-models/habilitar`
        : `${API_BASE_URL}/api/config/marcas-subgrupos-models/desabilitar`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_brand: idBrand, id_subgroup: idSubgroup }),
      });
      const result = await res.json().catch(() => ({}));
      if (!result.success) setRows(prevRows);
    } catch {
      setRows(prevRows);
    } finally {
      setPendingKey(null);
    }
  };

  const toggleGroup = (fab) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  };

  const toggleLinkedGroup = (fab) => {
    setLinkedCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(fab)) next.delete(fab);
      else next.add(fab);
      return next;
    });
  };

  const toggleGroupSelection = async (g) => {
    const allEnabled = g.subgrupos.every((s) => s.enabled);
    const targets = allEnabled
      ? g.subgrupos.filter((s) => s.enabled)
      : g.subgrupos.filter((s) => !s.enabled);
    for (const s of targets) {
      // eslint-disable-next-line no-await-in-loop
      await setEnabled(g.id_brand, s.id_subgroup, !allEnabled);
    }
  };

  const clearBrandEnabled = async (g) => {
    const brandBusy = g.subgrupos.some(
      (s) =>
        pendingKey === rowKey(g.id_brand, s.id_subgroup) ||
        isSubgroupRowPending(s.id_subgroup)
    );
    if (brandBusy) return;
    for (const s of g.subgrupos) {
      // eslint-disable-next-line no-await-in-loop
      await setEnabled(g.id_brand, s.id_subgroup, false);
    }
  };

  const clearFilters = () => {
    setFabricantes([]);
    setTecnologias([]);
    setBusca('');
    setBuscaDeb('');
  };

  const handleToggleFab = (value) => {
    setFabricantes((prev) =>
      prev.includes(value) ? prev.filter((m) => m !== value) : [...prev, value]
    );
  };

  const handleFabBulkToggle = () => {
    const visible = filteredFabItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => fabricantes.includes(v));
    setFabricantes((prev) =>
      allSelected
        ? prev.filter((m) => !visible.includes(m))
        : Array.from(new Set([...prev, ...visible]))
    );
  };

  const handleToggleTec = (value) => {
    setTecnologias((prev) =>
      prev.includes(value) ? prev.filter((t) => t !== value) : [...prev, value]
    );
  };

  const handleTecBulkToggle = () => {
    const visible = filteredTecItems.map((i) => i.value);
    if (!visible.length) return;
    const allSelected = visible.every((v) => tecnologias.includes(v));
    setTecnologias((prev) =>
      allSelected
        ? prev.filter((t) => !visible.includes(t))
        : Array.from(new Set([...prev, ...visible]))
    );
  };

  if (loading) {
    return <ProcessConfigSkeleton variant="table" ariaLabel="Carregando especialidade" />;
  }

  return (
    <div className="pcs2-page mscad-page">
      <Pcs2Section
        className="mscad-section-table"
        icon={IconCertificate}
        title="Configuração de equipamentos atendidos"
        desc="Fabricantes e tecnologias controlados no Verve"
      >
        <Pcs2Subblock
          title="Definição de equipamentos a especialidade de defeitos"
          desc="Configure fabricante e tecnologias que serão exibidos em especialidade de defeitos"
        >
          <div className="mscad-equip-panel">
            <div className="deq-filters mscad-filters">
              <EcClickUpFilterChip
                anchorRef={fabBtnRef}
                label="Fabricante"
                value={fabricantes.length > 0 ? String(fabricantes.length) : null}
                icon={IconBuilding}
                hasSelection={fabricantes.length > 0}
                isOpen={fabOpen}
                ariaLabel="Filtrar por fabricante"
                onClick={() => {
                  setTecOpen(false);
                  setFabOpen((o) => !o);
                }}
              />
              {fabOpen && fabPopoverStyle
                ? ReactDOM.createPortal(
                    <div
                      data-mscad-fab-popover
                      className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                      style={withMscadPopoverStyle(fabPopoverStyle)}
                      role="dialog"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <BoardSearchSimplePickerPanel
                        searchPlaceholder="Buscar fabricante..."
                        searchTerm={fabSearch}
                        onSearchChange={setFabSearch}
                        items={filteredFabItems}
                        getKey={(item) => item.value}
                        getLabel={(item) => item.label}
                        selectedKeys={fabricantes}
                        onToggle={(item) => handleToggleFab(item.value)}
                        emptyMessage="Nenhum fabricante"
                        listAriaLabel="Fabricantes"
                        bulkMenuEnabled
                        onBulkToggleAll={handleFabBulkToggle}
                      />
                    </div>,
                    document.body
                  )
                : null}

              <EcClickUpFilterChip
                anchorRef={tecBtnRef}
                label="Tecnologia"
                value={tecnologias.length > 0 ? String(tecnologias.length) : null}
                icon={IconStack2}
                hasSelection={tecnologias.length > 0}
                isOpen={tecOpen}
                ariaLabel="Filtrar por tecnologia"
                onClick={() => {
                  setFabOpen(false);
                  setTecOpen((o) => !o);
                }}
              />
              {tecOpen && tecPopoverStyle
                ? ReactDOM.createPortal(
                    <div
                      data-mscad-tec-popover
                      className={`${EC_FILTER_POPOVER_CLASS} board-search-filters-popover--simple board-search-filters-popover--cidade deq-filter-popover`}
                      style={withMscadPopoverStyle(tecPopoverStyle)}
                      role="dialog"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <BoardSearchSimplePickerPanel
                        searchPlaceholder="Buscar tecnologia..."
                        searchTerm={tecSearch}
                        onSearchChange={setTecSearch}
                        items={filteredTecItems}
                        getKey={(item) => item.value}
                        getLabel={(item) => item.label}
                        selectedKeys={tecnologias}
                        onToggle={(item) => handleToggleTec(item.value)}
                        emptyMessage="Nenhuma tecnologia"
                        listAriaLabel="Tecnologias"
                        bulkMenuEnabled
                        onBulkToggleAll={handleTecBulkToggle}
                      />
                    </div>,
                    document.body
                  )
                : null}

              <BoardToolbarExpandSearch
                value={busca}
                onChange={setBusca}
                placeholder="Buscar fabricante ou tecnologia..."
                ariaLabel="Buscar fabricante ou tecnologia"
              />

              {hasFilters ? (
                <button type="button" className="deq-clear-filters" onClick={clearFilters}>
                  Limpar filtros
                </button>
              ) : null}

              {canShowList ? (
                <span className="deq-filter-count">
                  {filteredByBrand.reduce((n, g) => n + g.subgrupos.length, 0)} tecnologias
                </span>
              ) : null}
            </div>

            <div className="deq-main mscad-main">
              <div className="deq-available mscad-available">
                <div className="deq-scroll mscad-scroll mscad-scroll--available">
                  {!canShowList ? (
                    <MscadFiltersEmptyState />
                  ) : filteredByBrand.length === 0 ? (
                    <div className="deq-empty">
                      <div className="deq-empty-title">Nenhum resultado encontrado</div>
                      <div className="deq-empty-hint">Ajuste os filtros para ver mais resultados.</div>
                    </div>
                  ) : (
                    filteredByBrand.map((g) => {
                      const fab = g.brand_nome;
                      const isCollapsed = collapsed.has(fab);
                      const enabledCount = g.subgrupos.filter((s) => s.enabled).length;
                      const allFabEnabled = g.subgrupos.every((s) => s.enabled);
                      const brandBusy = g.subgrupos.some(
                        (s) =>
                          pendingKey === rowKey(g.id_brand, s.id_subgroup) ||
                          isSubgroupRowPending(s.id_subgroup)
                      );

                      return (
                        <div key={g.id_brand}>
                          <button type="button" className="deq-brand-head" onClick={() => toggleGroup(fab)}>
                            <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                              {isCollapsed ? (
                                <ChevronRight size={13} strokeWidth={2} />
                              ) : (
                                <ChevronDown size={13} strokeWidth={2} />
                              )}
                            </span>
                            <MarcaAvatar fabricante={fab} />
                            <span className="deq-brand-label">{fab}</span>
                            <span className="deq-brand-meta">
                              {g.subgrupos.length}
                              {enabledCount > 0 ? ` · ${enabledCount} hab.` : ''}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="deq-brand-sel-all"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!brandBusy) toggleGroupSelection(g);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!brandBusy) toggleGroupSelection(g);
                                }
                              }}
                            >
                              {allFabEnabled ? 'rem. todos' : 'sel. todos'}
                            </span>
                          </button>

                          {!isCollapsed
                            ? g.subgrupos.map((s) => {
                                const pk = rowKey(g.id_brand, s.id_subgroup);
                                const busy =
                                  pendingKey === pk || isSubgroupRowPending(s.id_subgroup);
                                return (
                                  <div
                                    key={pk}
                                    className={`deq-model-row${s.enabled ? ' deq-model-row--linked' : ''}`}
                                    onClick={() => !busy && setEnabled(g.id_brand, s.id_subgroup, !s.enabled)}
                                  >
                                    <MscadCheckbox
                                      checked={s.enabled}
                                      disabled={busy}
                                      onClick={() => setEnabled(g.id_brand, s.id_subgroup, !s.enabled)}
                                      title={`Habilitar ${s.subgroup_nome}`}
                                    />
                                    <span className="deq-model-name" title={s.subgroup_nome}>
                                      {s.subgroup_nome}
                                    </span>
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="deq-linked-panel mscad-linked-panel">
                <div className="deq-linked-head">
                  <span className="deq-linked-title">Configurados · {enabledCount}</span>
                </div>
                <div className="deq-scroll mscad-scroll mscad-scroll--linked">
                  {enabledByBrand.length === 0 ? (
                    <div className="deq-empty">
                      <div className="deq-empty-title">Nenhuma tecnologia configurada</div>
                      <div className="deq-empty-hint">Selecione à esquerda para habilitar.</div>
                    </div>
                  ) : (
                    enabledByBrand.map((g) => {
                      const fab = g.brand_nome;
                      const isLinkedCollapsed = linkedCollapsed.has(fab);
                      const brandBusy = g.subgrupos.some(
                        (s) =>
                          pendingKey === rowKey(g.id_brand, s.id_subgroup) ||
                          isSubgroupRowPending(s.id_subgroup)
                      );
                      return (
                        <div key={`linked-${g.id_brand}`}>
                          <button
                            type="button"
                            className="deq-brand-head deq-brand-head--linked"
                            onClick={() => toggleLinkedGroup(fab)}
                          >
                            <span className="deq-lead-slot deq-brand-chevron" aria-hidden>
                              {isLinkedCollapsed ? (
                                <ChevronRight size={13} strokeWidth={2} />
                              ) : (
                                <ChevronDown size={13} strokeWidth={2} />
                              )}
                            </span>
                            <MarcaAvatar fabricante={fab} />
                            <span className="deq-brand-label">{fab}</span>
                            <span className="deq-brand-meta">{g.subgrupos.length}</span>
                            <span
                              role="button"
                              tabIndex={0}
                              className="mscad-brand-clear-all"
                              title="Remover todas as tecnologias deste fabricante"
                              aria-label={`Remover todas as tecnologias de ${fab}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!brandBusy) clearBrandEnabled(g);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!brandBusy) clearBrandEnabled(g);
                                }
                              }}
                            >
                              <X size={13} strokeWidth={2} aria-hidden />
                            </span>
                          </button>
                          {!isLinkedCollapsed
                            ? g.subgrupos.map((s) => {
                                const pk = rowKey(g.id_brand, s.id_subgroup);
                                const busy =
                                  pendingKey === pk || isSubgroupRowPending(s.id_subgroup);
                                return (
                                  <div
                                    key={pk}
                                    className="deq-linked-row"
                                    onClick={() => !busy && setEnabled(g.id_brand, s.id_subgroup, false)}
                                  >
                                    <span className="deq-linked-name" title={s.subgroup_nome}>
                                      {s.subgroup_nome}
                                    </span>
                                    <X className="deq-linked-remove" size={13} strokeWidth={2} aria-hidden />
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </Pcs2Subblock>
      </Pcs2Section>
    </div>
  );
}
